<script setup>
import { ref, watch, onMounted } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: { type: Object, default: null },
});

const isLoading = ref(false);
const analytics = ref(null);
const period = ref(30);

async function fetchAnalytics() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const { data } = await InstagramMediaReportsAPI.getAnalytics({
      inboxId: props.selectedInbox.id,
      period: period.value,
    });
    analytics.value = data;
  } catch (e) {
    console.error('Analytics error:', e);
  } finally {
    isLoading.value = false;
  }
}

function maxSentimentValue() {
  if (!analytics.value?.sentiment_timeline?.length) return 1;
  return Math.max(
    ...analytics.value.sentiment_timeline.map(
      d => Math.max(d.positive || 0, d.negative || 0, d.neutral || 0)
    ),
    1
  );
}

function barHeight(value) {
  return Math.max((value / maxSentimentValue()) * 100, 2);
}

function formatDay(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  return `${parts[2]}/${parts[1]}`;
}

function maxWordValue() {
  if (!analytics.value?.word_cloud?.length) return 1;
  return analytics.value.word_cloud[0]?.value || 1;
}

function wordSize(value) {
  const ratio = value / maxWordValue();
  return Math.max(Math.round(12 + ratio * 28), 12);
}

function wordColor(index) {
  const colors = [
    'text-n-blue-11', 'text-n-teal-11', 'text-n-violet-11', 'text-n-amber-11',
    'text-n-ruby-11', 'text-n-slate-11', 'text-n-blue-9', 'text-n-teal-9',
  ];
  return colors[index % colors.length];
}

watch(() => props.selectedInbox, fetchAnalytics);
watch(period, fetchAnalytics);
onMounted(fetchAnalytics);
</script>

<template>
  <div class="p-4">
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xl font-bold text-n-slate-12">Painel Político</h2>
      <div class="flex gap-2">
        <button v-for="p in [7, 15, 30]" :key="p"
          class="px-3 py-1 text-xs rounded-lg border transition-colors"
          :class="period === p ? 'bg-n-blue-9 text-white border-n-blue-9' : 'bg-n-solid-2 text-n-slate-11 border-n-weak'"
          @click="period = p">
          {{ p }}d
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-12"><Spinner size="large" /></div>

    <template v-if="analytics && !isLoading">
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
          <p class="text-xs text-n-slate-11 mb-1">Total Alertas</p>
          <p class="text-2xl font-bold text-n-slate-12">{{ analytics.summary.total_alerts }}</p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
          <p class="text-xs text-n-slate-11 mb-1">Positivos</p>
          <p class="text-2xl font-bold text-n-teal-11">{{ analytics.summary.total_positive }}</p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
          <p class="text-xs text-n-slate-11 mb-1">Negativos</p>
          <p class="text-2xl font-bold text-n-ruby-11">{{ analytics.summary.total_negative }}</p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
          <p class="text-xs text-n-slate-11 mb-1">Alto Risco</p>
          <p class="text-2xl font-bold text-n-orange-11">{{ analytics.summary.total_high_risk }}</p>
        </div>
        <div class="bg-n-solid-2 rounded-lg p-3 border border-n-weak text-center">
          <p class="text-xs text-n-slate-11 mb-1">Média/Dia</p>
          <p class="text-2xl font-bold text-n-blue-11">{{ analytics.summary.avg_daily_alerts }}</p>
        </div>
      </div>

      <!-- Sentiment Timeline Chart -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak mb-6">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Sentimento ao Longo do Tempo</h3>
        <div v-if="analytics.sentiment_timeline.length" class="flex items-end gap-1 h-48 overflow-x-auto pb-6">
          <div v-for="day in analytics.sentiment_timeline" :key="day.date" class="flex flex-col items-center gap-0.5 min-w-[28px]">
            <div class="flex items-end gap-px h-36">
              <div class="w-2 bg-n-teal-9 rounded-t transition-all" :style="{ height: barHeight(day.positive) + '%' }" :title="`Positivo: ${day.positive}`"></div>
              <div class="w-2 bg-n-slate-7 rounded-t transition-all" :style="{ height: barHeight(day.neutral) + '%' }" :title="`Neutro: ${day.neutral}`"></div>
              <div class="w-2 bg-n-ruby-9 rounded-t transition-all" :style="{ height: barHeight(day.negative) + '%' }" :title="`Negativo: ${day.negative}`"></div>
            </div>
            <span class="text-[9px] text-n-slate-9 whitespace-nowrap">{{ formatDay(day.date) }}</span>
          </div>
        </div>
        <div v-else class="text-center text-n-slate-11 py-8">Sem dados no período</div>
        <div class="flex gap-4 mt-2">
          <span class="flex items-center gap-1 text-xs text-n-slate-11"><span class="w-3 h-3 rounded bg-n-teal-9"></span> Positivo</span>
          <span class="flex items-center gap-1 text-xs text-n-slate-11"><span class="w-3 h-3 rounded bg-n-slate-7"></span> Neutro</span>
          <span class="flex items-center gap-1 text-xs text-n-slate-11"><span class="w-3 h-3 rounded bg-n-ruby-9"></span> Negativo</span>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Participação de Voz -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Participação de Voz</h3>
          <div v-if="analytics.share_of_voice.length" class="space-y-2">
            <div v-for="profile in analytics.share_of_voice.slice(0, 10)" :key="profile.username" class="flex items-center gap-3">
              <span class="text-xs text-n-slate-11 w-32 truncate">@{{ profile.username }}</span>
              <div class="flex-1 h-5 bg-n-solid-3 rounded-full overflow-hidden">
                <div class="h-full rounded-full transition-all"
                  :class="profile.profile_type === 'candidate' ? 'bg-n-teal-9' : profile.profile_type === 'allied' ? 'bg-n-green-9' : profile.profile_type === 'adversary' ? 'bg-n-ruby-9' : 'bg-n-blue-9'"
                  :style="{ width: profile.percentage + '%' }">
                </div>
              </div>
              <span class="text-xs font-medium text-n-slate-12 w-16 text-right">{{ profile.percentage }}%</span>
            </div>
          </div>
        </div>

        <!-- Word Cloud -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Nuvem de Palavras</h3>
          <div v-if="analytics.word_cloud.length" class="flex flex-wrap gap-2 items-center justify-center min-h-[200px]">
            <span v-for="(word, idx) in analytics.word_cloud.slice(0, 40)" :key="word.text"
              class="font-medium cursor-default transition-opacity hover:opacity-70"
              :class="wordColor(idx)"
              :style="{ fontSize: wordSize(word.value) + 'px' }"
              :title="`${word.text}: ${word.value} menções`">
              {{ word.text }}
            </span>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Engagement Comparison -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Comparativo de Engajamento</h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-n-slate-9 border-b border-n-weak">
                  <th class="pb-2">Perfil</th>
                  <th class="pb-2 text-right">Posts</th>
                  <th class="pb-2 text-right">Engajamento</th>
                  <th class="pb-2 text-right">Média</th>
                  <th class="pb-2 text-right">% Pos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="profile in analytics.engagement_comparison.slice(0, 10)" :key="profile.username" class="border-b border-n-weak/50">
                  <td class="py-2">
                    <span class="text-xs px-1.5 py-0.5 rounded-full text-white mr-1"
                      :class="profile.profile_type === 'candidate' ? 'bg-n-teal-9' : profile.profile_type === 'leadership' ? 'bg-n-blue-9' : profile.profile_type === 'allied' ? 'bg-n-green-9' : 'bg-n-ruby-9'">
                      {{ profile.profile_type === 'candidate' ? 'C' : profile.profile_type === 'leadership' ? 'L' : profile.profile_type === 'allied' ? 'AL' : 'A' }}
                    </span>
                    @{{ profile.username }}
                  </td>
                  <td class="py-2 text-right text-n-slate-11">{{ profile.total_posts }}</td>
                  <td class="py-2 text-right font-medium text-n-slate-12">{{ profile.total_engagement.toLocaleString() }}</td>
                  <td class="py-2 text-right text-n-slate-11">{{ profile.avg_engagement.toLocaleString() }}</td>
                  <td class="py-2 text-right">
                    <span class="text-xs px-1.5 py-0.5 rounded-full" :class="profile.positive_pct > 60 ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-slate-3 text-n-slate-11'">
                      {{ profile.positive_pct }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Platform Distribution -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Distribuição por Plataforma</h3>
          <div v-if="analytics.platform_distribution.length" class="space-y-3">
            <div v-for="platform in analytics.platform_distribution" :key="platform.platform" class="flex items-center gap-3">
              <span class="text-sm font-medium text-n-slate-12 w-20 capitalize">{{ platform.platform }}</span>
              <div class="flex-1 h-6 bg-n-solid-3 rounded-full overflow-hidden">
                <div class="h-full rounded-full transition-all bg-n-violet-9"
                  :style="{ width: (platform.count / analytics.summary.total_alerts * 100) + '%' }">
                </div>
              </div>
              <span class="text-sm font-medium text-n-slate-12 w-12 text-right">{{ platform.count }}</span>
            </div>
          </div>

          <!-- Top Posts -->
          <h4 class="text-md font-semibold mt-6 mb-3 text-n-slate-12">Posts com Maior Engajamento</h4>
          <div class="space-y-2">
            <div v-for="post in (analytics.top_posts || []).slice(0, 5)" :key="post.permalink" class="p-2 rounded border border-n-weak/50 text-xs">
              <div class="flex items-center gap-2 mb-1">
                <span class="font-medium text-n-slate-12">{{ post.engagement?.toLocaleString() }} eng</span>
                <span class="px-1.5 py-0.5 rounded-full text-white" :class="post.sentiment === 'positive' ? 'bg-n-teal-9' : post.sentiment === 'negative' ? 'bg-n-ruby-9' : 'bg-n-slate-9'">{{ post.sentiment }}</span>
                <span class="text-n-slate-9">@{{ post.profile_username }}</span>
              </div>
              <p class="text-n-slate-11 truncate">{{ post.caption }}</p>
              <a :href="post.permalink" target="_blank" class="text-n-blue-11 hover:underline text-[10px]">Abrir post</a>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
